import React, { useState } from "react";
import { CalendarIcon, Clock } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

const WINDOWS = [
  { value: "8a–10a", label: "8a – 10a" },
  { value: "10a–12p", label: "10a – 12p" },
  { value: "12p–2p", label: "12p – 2p" },
  { value: "2p–4p", label: "2p – 4p" },
  { value: "4p–6p", label: "4p – 6p" },
  { value: "6p–8p", label: "6p – 8p" },
];

const isDateDisabled = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const max = new Date();
  max.setDate(max.getDate() + 90);
  if (date < today || date > max) return true;
  // Only allow Saturday (6) and Sunday (0)
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

export const BookingPicker = ({
  preferredDate,
  onDateChange,
  preferredWindow,
  onWindowChange,
}) => {
  const [dateOpen, setDateOpen] = useState(false);

  return (
    <div
      data-testid="booking-picker"
      className="border-2 border-navy p-5 bg-offwhite"
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="h-6 w-6 flex items-center justify-center bg-gold text-navy border-2 border-navy">
          <CalendarIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
        <span className="font-display uppercase text-sm tracking-[0.18em] text-navy">
          Pick a time (optional)
        </span>
        <span className="ml-auto inline-flex items-center gap-1 font-body text-[10px] uppercase tracking-[0.18em] text-navy/60">
          <Clock className="h-3 w-3" strokeWidth={2.5} />
          Weekends only
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <span className="block font-body text-[11px] font-bold uppercase tracking-[0.18em] text-navy/70 mb-2">
            Preferred Date
          </span>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="preferred-date-btn"
                className="w-full flex items-center justify-between gap-2 bg-white border-2 border-navy px-4 py-3 font-body text-sm text-left hover:bg-offwhite transition-colors"
              >
                <span className={preferredDate ? "text-navy" : "text-navy/50"}>
                  {preferredDate
                    ? format(preferredDate, "EEE, MMM d, yyyy")
                    : "Any date works"}
                </span>
                <CalendarIcon
                  className="h-4 w-4 text-navy shrink-0"
                  strokeWidth={2.5}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0 bg-white border-2 border-navy rounded-none"
              align="start"
            >
              <Calendar
                mode="single"
                selected={preferredDate}
                onSelect={(d) => {
                  onDateChange(d);
                  setDateOpen(false);
                }}
                disabled={isDateDisabled}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {preferredDate && (
            <button
              type="button"
              onClick={() => onDateChange(null)}
              className="mt-1 text-[10px] uppercase tracking-wider text-navy/60 hover:text-gold"
            >
              Clear date
            </button>
          )}
        </div>

        <div>
          <span className="block font-body text-[11px] font-bold uppercase tracking-[0.18em] text-navy/70 mb-2">
            2-Hour Window
          </span>
          <div
            data-testid="preferred-window-group"
            className="grid grid-cols-3 gap-0 border-2 border-navy bg-white"
          >
            {WINDOWS.map((w, i) => {
              const active = preferredWindow === w.value;
              const isRightEdge = (i + 1) % 3 === 0;
              const isBottomRow = i >= WINDOWS.length - 3;
              return (
                <button
                  key={w.value}
                  type="button"
                  data-testid={`window-${w.value.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  onClick={() => onWindowChange(active ? "" : w.value)}
                  className={`flex items-center justify-center px-2 py-3 transition-colors ${
                    active
                      ? "bg-navy text-gold"
                      : "bg-white text-navy hover:bg-offwhite"
                  } ${!isRightEdge ? "border-r-2 border-navy" : ""} ${
                    !isBottomRow ? "border-b-2 border-navy" : ""
                  }`}
                >
                  <span className="font-display uppercase text-[11px] sm:text-xs tracking-wider whitespace-nowrap">
                    {w.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {(preferredDate || preferredWindow) && (
        <p
          data-testid="booking-summary"
          className="mt-3 text-[11px] text-navy/75 font-body"
        >
          <span className="font-bold uppercase tracking-wider text-[10px] text-navy">
            You'd like:
          </span>{" "}
          {preferredDate ? format(preferredDate, "EEE, MMM d") : "Any day"} ·{" "}
          {preferredWindow || "any time"}
        </p>
      )}
    </div>
  );
};
